import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast, Toaster } from "sonner";
import { useNavigate } from "react-router-dom";
import { Ticket, CreditCard } from "lucide-react";

export default function Viewer() {
  const [game, setGame] = useState(null);
  const [name, setName] = useState(localStorage.getItem("viewer_name") || "");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cfg, setCfg] = useState({ email_enabled: false, sms_enabled: false, ideal_enabled: true, test_mode: false });
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState("ideal");
  const [busy, setBusy] = useState(false);
  const [myCards, setMyCards] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/games/current").then((r) => setGame(r.data)).catch(() => {});
    api.get("/config").then((r) => { setCfg(r.data); if (!r.data.ideal_enabled) setMethod("card"); }).catch(() => {});
    const saved = JSON.parse(localStorage.getItem("my_tickets") || "[]");
    if (saved.length) {
      Promise.all([api.get(`/tickets/batch?ids=${saved.join(",")}`), api.get("/games")]).then(([t, g]) => {
        const games = Object.fromEntries(g.data.map((x) => [x.id, x]));
        const cards = t.data.map((tk) => ({ ...tk, game: games[tk.game_id] })).filter((tk) => tk.paid);
        cards.sort((a, b) => (b.created_at > a.created_at ? 1 : -1));
        setMyCards(cards);
      }).catch(() => {});
    }
  }, []);

  const buy = async () => {
    if (!name.trim()) { toast.error("Vul je naam in"); return; }
    if (!game) { toast.error("Geen actief spel"); return; }
    setBusy(true);
    localStorage.setItem("viewer_name", name);
    try {
      const r = await api.post("/tickets/purchase", {
        game_id: game.id,
        display_name: name,
        email: email || null,
        quantity: qty,
        origin_url: window.location.origin,
        payment_method: method,
        phone: cfg.sms_enabled && phone ? phone : null,
      });
      const existing = JSON.parse(localStorage.getItem("my_tickets") || "[]");
      localStorage.setItem("my_tickets", JSON.stringify([...existing, ...r.data.ticket_ids]));
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aankoop mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E1118] text-slate-100">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="max-w-lg mx-auto p-6 py-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.3em] text-slate-300 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#FF1744] animate-pulse" /> Speel Live Mee
        </div>
        <h1 className="text-4xl font-black leading-tight" style={{ fontFamily: "Unbounded, sans-serif" }}>
          Koop je <span className="text-[#FFD700]">bingokaart</span>
        </h1>
        <p className="mt-3 text-slate-400">
          {game ? <>Ronde: <b className="text-slate-200">{game.title}</b> — €{game.ticket_price.toFixed(2)} per kaart</> : "Geen actief spel op dit moment."}
        </p>

        {game && (
          <div className="mt-8 p-6 rounded-2xl bg-[#12141D] border border-white/10 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Jouw naam (wordt in de studio getoond)</label>
              <Input data-testid="viewer-name" value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Naam" className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">E-mail {cfg.email_enabled ? "(je ontvangt de kaartlink per mail)" : "(optioneel)"}</label>
              <Input data-testid="viewer-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="jij@voorbeeld.nl" className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
            </div>
            {cfg.sms_enabled && (
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Mobiel nummer (optioneel, kaartlink per SMS)</label>
                <Input data-testid="viewer-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                       placeholder="06 12345678" className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Aantal kaarten</label>
              <div className="mt-1 flex gap-2">
                {[1, 2, 3, 5].map((q) => (
                  <button key={q} data-testid={`qty-${q}`} onClick={() => setQty(q)}
                          className={`flex-1 py-3 rounded-lg font-bold border ${qty === q ? "bg-[#FFD700] text-[#0B0C10] border-[#FFD700]" : "bg-[#0B0C10] border-white/10 text-slate-300"}`}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400">Betaalmethode</label>
              <div className={`mt-1 grid gap-2 ${cfg.ideal_enabled ? "grid-cols-2" : "grid-cols-1"}`}>
                {cfg.ideal_enabled && (
                <button data-testid="method-ideal" onClick={() => setMethod("ideal")}
                        className={`p-3 rounded-lg border text-left transition ${method === "ideal" ? "bg-[#CC0066]/15 border-[#CC0066] ring-1 ring-[#CC0066]" : "bg-[#0B0C10] border-white/10"}`}>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-9 h-6 rounded bg-[#CC0066] text-white text-[10px] font-black tracking-tight">iDEAL</span>
                    <span className="font-bold text-sm">iDEAL | Wero</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">Betaal met je eigen bank-app</div>
                </button>
                )}
                <button data-testid="method-card" onClick={() => setMethod("card")}
                        className={`p-3 rounded-lg border text-left transition ${method === "card" ? "bg-[#00F0FF]/10 border-[#00F0FF] ring-1 ring-[#00F0FF]" : "bg-[#0B0C10] border-white/10"}`}>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#00F0FF]" />
                    <span className="font-bold text-sm">{cfg.ideal_enabled ? "Creditcard" : "Kaart of bank"}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">{cfg.ideal_enabled ? "Visa, Mastercard, Amex" : "Visa, Mastercard, Bancontact en meer"}</div>
                </button>
              </div>
              {!cfg.ideal_enabled && <p className="text-[11px] text-slate-500 mt-1" data-testid="ideal-unavailable">iDEAL is nog niet geactiveerd voor dit account.</p>}
            </div>
            <div className="pt-3 border-t border-white/10 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400">Totaal</div>
                <div className="text-3xl font-black text-[#FFD700]" data-testid="viewer-total">€{(game.ticket_price * qty).toFixed(2)}</div>
              </div>
              <Button data-testid="viewer-buy" onClick={buy} disabled={busy}
                      className="h-14 px-6 bg-gradient-to-r from-[#FFD700] to-[#FF007F] text-[#0B0C10] font-black hover:opacity-90">
                <Ticket className="w-5 h-5 mr-2" /> {busy ? "Bezig..." : "Koop nu"}
              </Button>
            </div>
            <p className="text-xs text-slate-500" data-testid="payment-hint">
              {cfg.test_mode
                ? (method === "ideal"
                  ? "Testmodus: je komt op een nep-bankpagina van Stripe; klik daar op \"Authorize test payment\"."
                  : "Testmodus: gebruik kaartnummer 4242 4242 4242 4242, willekeurige toekomstige datum en CVC.")
                : "Je wordt doorgestuurd naar de beveiligde betaalpagina van Stripe. Na betaling staat je kaart direct klaar."}
            </p>
          </div>
        )}

        {myCards.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Mijn kaarten</div>
            <div className="space-y-2">
              {myCards.map((t) => {
                const finished = !t.game || t.game.status === "finished";
                const isCurrent = game && t.game_id === game.id;
                return (
                  <button key={t.id} onClick={() => nav(`/viewer/play/${t.id}`)}
                          data-testid={`open-ticket-${t.id}`}
                          className={`w-full p-4 rounded-xl border text-left transition ${
                            isCurrent ? "bg-[#12141D] border-[#FFD700]/60 hover:border-[#FFD700]" :
                            finished ? "bg-[#12141D]/50 border-white/5 opacity-60" : "bg-[#12141D] border-white/10 hover:border-[#FFD700]/50"
                          }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold text-sm">{t.game?.title || "Onbekend spel"} <span className="text-slate-500 font-normal">· {t.display_name}</span></div>
                        <div className="font-mono text-xs text-slate-500 mt-0.5">{t.id.slice(0, 8)}...</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                        isCurrent ? "bg-[#00E676]/20 text-[#00E676]" : finished ? "bg-white/10 text-slate-400" : "bg-[#FFD700]/20 text-[#FFD700]"
                      }`}>{isCurrent ? "Speel nu" : finished ? "Afgelopen" : t.game.status}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
